import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ReportsService } from '../../services/reports/reports.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import Moment from 'moment-timezone';

@Component({
  selector: 'app-reports-map',
  templateUrl: './reports-map.component.html',
  styleUrls: ['./reports-map.component.scss']
})
export class ReportsMapComponent implements OnInit, OnDestroy {

  public allReports: any[] = [];
  public reports: any[] = [];
  public isLoading: boolean = true;
  public total: number = 0;

  // Pin seleccionado (datos ligeros del listado)
  public selectedReport: any = null;

  // Detalle completo cargado desde API
  public detailReport: any = null;
  public isLoadingDetail: boolean = false;
  public detailPanelOpen: boolean = false;

  public mapLat: number = 19.1167;
  public mapLng: number = -96.1333;
  public mapZoom: number = 13;

  public startDate: string;
  public endDate: string;

  // Filtros
  public filtersOpen: boolean = true;
  public filterStatus: string = '';
  public filterSubject: string = '';
  public filterQuickAccess: boolean = false;

  public statusOptions: string[] = [
    'Pendiente', 'En proceso', 'En desarrollo', 'Atendida', 'Resuelto', 'Cerrado'
  ];
  public subjectOptions: string[] = [];

  private subs: Array<Subscription> = [];

  constructor(
    private reportsService: ReportsService,
    private toastr: ToastrService,
    public router: Router
  ) {}

  ngOnInit(): void {
    const today = Moment().format('YYYY-MM-DD');
    const from = Moment().subtract(30, 'days').format('YYYY-MM-DD');
    this.startDate = from;
    this.endDate = today;
    this.loadReports();
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  loadReports(): void {
    this.isLoading = true;
    this.selectedReport = null;
    this.closeDetail();
    this.subs.push(
      this.reportsService.getReportsSummaryMap(this.startDate, this.endDate).subscribe(
        data => {
          this.total = data.Total || 0;
          this.allReports = (data.List || []).filter((r: any) =>
            r.Location &&
            (
              (r.Location.lat !== undefined && r.Location.lng !== undefined &&
               r.Location.lat !== 0         && r.Location.lng !== 0) ||
              (r.Location.coordinates &&
               r.Location.coordinates[0] !== 0 && r.Location.coordinates[1] !== 0)
            )
          );
          const subjects = [...new Set(this.allReports.map((r: any) => r.Subject).filter(Boolean))].sort();
          this.subjectOptions = subjects as string[];
          this.applyFilters();
          this.isLoading = false;
        },
        error => {
          this.isLoading = false;
          this.toastr.error('No se pudieron cargar los reportes', 'Error', { timeOut: 2000 });
        }
      )
    );
  }

  applyFilters(): void {
    this.selectedReport = null;
    this.closeDetail();
    this.reports = this.allReports.filter((r: any) => {
      if (this.filterStatus && r.Status !== this.filterStatus) return false;
      if (this.filterSubject && r.Subject !== this.filterSubject) return false;
      if (this.filterQuickAccess && !r.QuickAccess) return false;
      return true;
    });
  }

  clearFilters(): void {
    this.filterStatus = '';
    this.filterSubject = '';
    this.filterQuickAccess = false;
    this.applyFilters();
  }

  toggleFilters(): void {
    this.filtersOpen = !this.filtersOpen;
  }

  // ── Marcador: clic rápido + carga detalle completo ──────────────────────────
  onMarkerClick(report: any): void {
    this.selectedReport = report;
    this.openDetail(report.id);
  }

  openDetail(id: number | string): void {
    this.detailPanelOpen = true;
    this.isLoadingDetail = true;
    this.detailReport = null;
    this.subs.push(
      this.reportsService.getReport(id).subscribe(
        data => {
          // Parsear ImgUrl de eventos igual que ReportDetailsComponent
          if (data.Event) {
            data.Event.forEach((item: any, idx: number) => {
              try {
                data.Event[idx].ImgUrl = JSON.parse(item.ImgUrl);
              } catch (_) {
                data.Event[idx].ImgUrl = item.ImgUrl ? [item.ImgUrl] : [];
              }
              item.fileTypes = [];
              (data.Event[idx].ImgUrl || []).forEach((file: string) => {
                const parts = file.split('.');
                item.fileTypes.push(parts[parts.length - 1]);
              });
            });
          }
          this.detailReport = data;
          this.isLoadingDetail = false;
        },
        error => {
          this.isLoadingDetail = false;
          this.toastr.error('No se pudo cargar el detalle', 'Error', { timeOut: 2000 });
        }
      )
    );
  }

  closeDetail(): void {
    this.detailPanelOpen = false;
    this.detailReport = null;
    this.selectedReport = null;
  }

  goToDetail(): void {
    if (this.detailReport) {
      this.router.navigateByUrl(`/reports/show/${this.detailReport.id}`);
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────
  getMarkerLat(report: any): number {
    if (report.Location.lat !== undefined) return report.Location.lat;
    return report.Location.coordinates[1];
  }

  getMarkerLng(report: any): number {
    if (report.Location.lng !== undefined) return report.Location.lng;
    return report.Location.coordinates[0];
  }

  // ── Simbología por Estado ─────────────────────────────────────────────────────
  static readonly STATUS_ICONS: Record<string, { icon: string; color: string; label: string }> = {
    'Pendiente':     { icon: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',    color: '#dc3545', label: 'Pendiente'    },
    'En proceso':    { icon: 'http://maps.google.com/mapfiles/ms/icons/orange-dot.png', color: '#fd7e14', label: 'En proceso'   },
    'En desarrollo': { icon: 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png', color: '#ffc107', label: 'En desarrollo'},
    'Atendida':      { icon: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',   color: '#0d6efd', label: 'Atendida'     },
    'Resuelto':      { icon: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png',  color: '#28a745', label: 'Resuelto'     },
    'Cerrado':       { icon: 'http://maps.google.com/mapfiles/ms/icons/purple-dot.png', color: '#6f42c1', label: 'Cerrado'      },
  };

  getMarkerIcon(report: any): string {
    if (this.selectedReport && this.selectedReport.id === report.id) {
      return 'http://maps.google.com/mapfiles/ms/icons/pink-dot.png';
    }
    return ReportsMapComponent.STATUS_ICONS[report.Status]?.icon
      ?? 'http://maps.google.com/mapfiles/ms/icons/red-dot.png';
  }

  // ── Conteo por estado (solo reportes filtrados) ───────────────────────────────
  get statusCounts(): { status: string; count: number; color: string }[] {
    const map: Record<string, number> = {};
    for (const r of this.reports) {
      map[r.Status] = (map[r.Status] || 0) + 1;
    }
    const order = ['Pendiente', 'En proceso', 'En desarrollo', 'Atendida', 'Resuelto', 'Cerrado'];
    return order
      .filter(s => map[s] > 0)
      .map(s => ({
        status: s,
        count:  map[s],
        color:  ReportsMapComponent.STATUS_ICONS[s]?.color ?? '#6c757d'
      }));
  }

  get legendEntries(): { label: string; color: string }[] {
    return Object.values(ReportsMapComponent.STATUS_ICONS);
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Pendiente':      return 'badge-warning';
      case 'En proceso':
      case 'En desarrollo':  return 'badge-info';
      case 'Atendida':
      case 'Resuelto':       return 'badge-success';
      case 'Cerrado':        return 'badge-secondary';
      default:               return 'badge-light';
    }
  }

  getPriorityClass(priority: string): string {
    switch (priority) {
      case 'Alta':
      case 'Urgente': return 'badge-danger';
      case 'Media':   return 'badge-warning';
      default:        return 'badge-secondary';
    }
  }

  formatDate(date: string): string {
    return date ? Moment(date).format('DD/MM/YYYY HH:mm') : '-';
  }

  get activeFiltersCount(): number {
    let count = 0;
    if (this.filterStatus) count++;
    if (this.filterSubject) count++;
    if (this.filterQuickAccess) count++;
    return count;
  }

  isImage(fileType: string): boolean {
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes((fileType || '').toLowerCase());
  }
}
